import {useCallback, useEffect, useRef, useState} from 'react'

import useTalkingPoints from './useTalkingPoints'
import useVoiceRecorder, {isLiveRecorderState, RECORDER_STATES} from './useVoiceRecorder'

export const READ_MODES = {SCRIPT: 'script', POINTS: 'points'}

// What a take ended as, for the note or the done screen that follows.
export const TAKE_RESULTS = {SAVED: 'saved', REPLACED: 'replaced', KEPT: 'kept'}

export const RECORD_VERSION_COPY = {
  title: 'Record this version',
  sub: 'Your family will hear you tell it in your own voice.',
  subWithCurrent: ' You\u2019ll hear it back and choose which recording to keep.',
  decideTitle: 'Use your new recording?',
  decideSub: 'Your current recording will be replaced. Listen to both before you choose.',
  hints: {
    script: 'Take your time. You can pause anytime, and going off script is fine.',
    points: 'Glance at each point, then tell it your own way. You can pause anytime.',
    paused: 'Paused. Pick up where you left off whenever you\u2019re ready.'
  },
  notes: {
    saved: 'Recording saved. Your family will hear it with this story.',
    replaced: 'Your new recording replaces your current one.',
    undone: 'Your previous recording is back. The new one is waiting above your story if you change your mind.',
    kept: 'You kept your current recording.',
    parked: 'Your current recording is still the one your family hears. Your new recording is saved above your story until you decide.'
  },
  announce: {
    recording: 'Recording. Press again to stop.',
    saved: 'Recording saved.',
    decide: 'Use your new recording? Your current recording will be replaced.',
    replaced: 'New recording saved. It replaces your current one.',
    undone: 'Previous recording restored.',
    kept: 'Kept your current recording.'
  }
}

/**
 * "Record this version" (TK-3204): recording the story as it reads now,
 * word for word or from talking points, with no dictation. Shared by the
 * desktop modal and the phone takeover's recording screens; only one of
 * them is up at a time.
 *
 * A first recording lands as the story's recording (`onSaveFirst`). When
 * one already exists the new take is saved as the story's draft at once
 * (`onDraft`, so nothing is lost to a reload) and waits (`take`,
 * `deciding`) until the writer chooses Use new (`onUseNew`) / Keep current
 * (`onKeepCurrent`), or closes and leaves it parked (`onParkDraft`, the
 * note only). The story data itself is the screen's; `{quiet}` says the
 * takeover's done screen will do the telling instead of a note.
 *
 * `onBegin` runs as any of these surfaces is about to show, so the screen
 * can stop the voice card's playback: left running under the recorder, it
 * would be picked up by the microphone alongside the new take.
 */
const useRecordVersion = ({
  getText,
  question,
  recording,
  userConfig,
  request,
  onBegin,
  onSaveFirst,
  onDraft,
  onUseNew,
  onKeepCurrent,
  onParkDraft,
  onError,
  onCapReached,
  onPauseChange,
  announce
}) => {
  const [open, setOpen] = useState(false)
  const [mode, setMode] = useState(READ_MODES.SCRIPT)
  const [take, setTake] = useState(null)
  const [deciding, setDeciding] = useState(false)
  // What the takeover's last take came to (TAKE_RESULTS); the modal tells
  // through notes instead and leaves this null.
  const [result, setResult] = useState(null)
  // 'modal' or 'takeover': which surface the recorder is speaking for.
  const surfaceRef = useRef('modal')
  const returnFocusRef = useRef(null)
  const recordingRef = useRef(recording)

  recordingRef.current = recording

  const talkingPoints = useTalkingPoints({getText, question, request})

  const handleTake = useCallback(newTake => {
    const quiet = surfaceRef.current === 'takeover'

    if (recordingRef.current) {
      onDraft(newTake)
      setTake(newTake)
      setDeciding(true)
      announce(RECORD_VERSION_COPY.announce.decide)

      return
    }

    onSaveFirst(newTake, {quiet})
    // The takeover's done screen does the telling (its heading takes focus);
    // announcing here too would read the same outcome twice.
    if (quiet) setResult(TAKE_RESULTS.SAVED)
    else announce(RECORD_VERSION_COPY.announce.saved)
    setOpen(false)
  }, [onSaveFirst, onDraft, announce])

  const recorder = useVoiceRecorder({
    userConfig,
    question,
    transcribe: false,
    onTranscript: () => {},
    onRecordingComplete: handleTake,
    onError,
    onCapReached,
    onPauseChange
  })

  // Waiting on the microphone counts too: the controls hold until the start
  // is granted, refused, or cancelled by closing.
  const recorderBusy = recorder.starting || isLiveRecorderState(recorder.recorderState) || recorder.recorderState === RECORDER_STATES.PROCESSING

  const restoreFocus = () => {
    const element = returnFocusRef.current

    returnFocusRef.current = null
    if (element && document.body.contains(element)) element.focus()
  }

  const begin = useCallback(surface => {
    if (onBegin) onBegin()
    surfaceRef.current = surface
    setMode(READ_MODES.SCRIPT)
    setTake(null)
    setDeciding(false)
    setResult(null)
  }, [onBegin])

  /** The desktop modal, ready to record. */
  const openRecorder = useCallback(() => {
    returnFocusRef.current = document.activeElement
    begin('modal')
    setOpen(true)
  }, [begin])

  /** The desktop modal on the decision for a parked draft take. */
  const openDecision = useCallback(draft => {
    returnFocusRef.current = document.activeElement
    begin('modal')
    setTake(draft)
    setDeciding(true)
    setOpen(true)
    announce(RECORD_VERSION_COPY.announce.decide)
  }, [begin, announce])

  /** The takeover's recording screens are about to show. */
  const beginTakeover = useCallback(() => begin('takeover'), [begin])

  const chooseMode = useCallback(nextMode => {
    if (recorderBusy) return

    setMode(nextMode)
    if (nextMode === READ_MODES.POINTS) talkingPoints.load()
  }, [recorderBusy, talkingPoints.load])

  const toggleRecording = useCallback(() => {
    if (isLiveRecorderState(recorder.recorderState)) {
      recorder.stopRecording()
    } else if (recorder.recorderState === RECORDER_STATES.IDLE && !recorder.starting) {
      recorder.startRecording()
      announce(RECORD_VERSION_COPY.announce.recording)
    }
  }, [recorder.recorderState, recorder.starting, recorder.startRecording, recorder.stopRecording, announce])

  const useNew = useCallback(() => {
    if (!take) return

    const quiet = surfaceRef.current === 'takeover'

    onUseNew(take, {quiet})
    setTake(null)
    setDeciding(false)
    if (quiet) setResult(TAKE_RESULTS.REPLACED)
    else announce(RECORD_VERSION_COPY.announce.replaced)
    setOpen(false)
    restoreFocus()
  }, [take, onUseNew, announce])

  const keepCurrent = useCallback(() => {
    const quiet = surfaceRef.current === 'takeover'

    onKeepCurrent({quiet})
    setTake(null)
    setDeciding(false)
    if (quiet) setResult(TAKE_RESULTS.KEPT)
    else announce(RECORD_VERSION_COPY.announce.kept)
    setOpen(false)
    restoreFocus()
  }, [onKeepCurrent, announce])

  /**
   * Closing without choosing never loses anything: an undecided take stays
   * parked as the story's draft, and the note says so. Not while recording;
   * a Start still waiting on the microphone is cancelled, so it can't begin
   * a recording behind a closed surface.
   */
  const close = useCallback(() => {
    if (recorder.starting) recorder.cancelStart()
    else if (recorderBusy) return

    if (deciding && take) onParkDraft(take)

    talkingPoints.reset()
    setTake(null)
    setDeciding(false)
    setOpen(false)
    restoreFocus()
  }, [recorder.starting, recorder.cancelStart, recorderBusy, deciding, take, onParkDraft, talkingPoints.reset])

  // A modal that just saved a first recording closes itself; give focus back.
  useEffect(() => {
    if (!open && returnFocusRef.current && surfaceRef.current === 'modal') restoreFocus()
  }, [open])

  return {
    open,
    mode,
    take,
    deciding,
    result,
    points: talkingPoints.points,
    pointsLoading: talkingPoints.loading,
    recorder: {
      state: recorder.recorderState,
      duration: recorder.duration,
      // `busy` holds the controls; `locked` is the part of it that also holds
      // the exits (a pending Start can be cancelled by closing, a take can't).
      busy: recorderBusy,
      locked: recorderBusy && !recorder.starting,
      starting: recorder.starting,
      cancelStart: recorder.cancelStart,
      supported: recorder.recordingSupported,
      toggle: toggleRecording,
      pause: recorder.pauseRecording,
      resume: recorder.resumeRecording
    },
    openRecorder,
    openDecision,
    beginTakeover,
    chooseMode,
    useNew,
    keepCurrent,
    close
  }
}

export default useRecordVersion
